import { Injectable } from '@angular/core';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ApplicationInsightsService {
  private appInsights: ApplicationInsights | null = null;
  private isEnabled: boolean = false;

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    const connectionString = environment.applicationInsightsConnectionString;

    if (!connectionString || connectionString.trim() === '') {
      this.isEnabled = false;
      return;
    }

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: connectionString,
        enableAutoRouteTracking: false,     // Disabled - using manual Angular Router integration
        autoTrackPageVisitTime: true,       // Track time spent on pages
        enableCorsCorrelation: true,        // CORS correlation for distributed tracing
        correlationHeaderExcludedDomains: [  // Exclude external APIs from correlation headers to prevent CORS errors
          '*.hereapi.com',
          '*.mixpanel.com',
          '*.applicationinsights.azure.com'
        ],
        enableRequestHeaderTracking: true,  // Track request headers
        enableResponseHeaderTracking: true, // Track response headers
        disableFetchTracking: false,        // Track fetch() API calls
        disableAjaxTracking: false,         // Track XMLHttpRequest calls
        disableExceptionTracking: false,    // Track uncaught exceptions
        enableDebug: false,
        loggingLevelConsole: 0,
        // User and session tracking
        enableSessionStorageBuffer: true,   // Store telemetry in session storage
        isStorageUseDisabled: false,        // Enable cookies for user/session tracking
        isCookieUseDisabled: false,         // Enable cookies
        cookieDomain: undefined,            // Use default domain
        disableInstrumentationKeyValidation: false
      }
    });

    this.appInsights.loadAppInsights();
    this.isEnabled = true;
  }

  /**
   * Track custom events (similar to Mixpanel pattern)
   * @param name Event name
   * @param properties Optional event properties
   */
  trackEvent(name: string, properties?: { [key: string]: any }): void {
    if (!this.isEnabled || !this.appInsights) return;

    this.appInsights.trackEvent({ name, properties });
  }

  /**
   * Track page views manually (automatic tracking is enabled by default)
   * @param name Optional page name
   * @param url Optional page URL
   * @param properties Optional page properties
   */
  trackPageView(name?: string, url?: string, properties?: { [key: string]: any }): void {
    if (!this.isEnabled || !this.appInsights) return;

    this.appInsights.trackPageView({ name, uri: url, properties });
  }

  /**
   * Track exceptions
   * @param error Error object
   * @param severityLevel Optional severity level (0=Verbose, 1=Information, 2=Warning, 3=Error, 4=Critical)
   * @param properties Optional exception properties
   */
  trackException(error: Error, severityLevel?: SeverityLevel, properties?: { [key: string]: any }): void {
    if (!this.isEnabled || !this.appInsights) return;

    this.appInsights.trackException({
      exception: error,
      severityLevel,
      properties
    });
  }

  /**
   * Track metrics
   * @param name Metric name
   * @param average Metric value
   * @param properties Optional metric properties
   */
  trackMetric(name: string, average: number, properties?: { [key: string]: any }): void {
    if (!this.isEnabled || !this.appInsights) return;

    this.appInsights.trackMetric({ name, average }, properties);
  }

  /**
   * Set authenticated user context
   * @param userId User ID (will be hashed by Application Insights)
   * @param accountId Optional account/tenant ID
   */
  setAuthenticatedUserContext(userId: string, accountId?: string): void {
    if (!this.isEnabled || !this.appInsights) return;

    this.appInsights.setAuthenticatedUserContext(userId, accountId, true);
  }

  /**
   * Clear user context on logout
   */
  clearAuthenticatedUserContext(): void {
    if (!this.isEnabled || !this.appInsights) return;

    this.appInsights.clearAuthenticatedUserContext();

    // Also clear the anonymous user ID cookie to generate a new one for the next user
    // This prevents the same anonymous ID from being shared across different users
    this.clearUserCookie();
  }

  /**
   * Clear Application Insights cookies to reset user ID
   * This ensures each user session gets a unique ID
   */
  private clearUserCookie(): void {
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      const cookieName = cookie.split('=')[0].trim();

      if (cookieName.startsWith('ai_user') || cookieName.startsWith('ai_session')) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      }
    }
  }

  /**
   * Get the underlying Application Insights instance (for advanced usage)
   * @returns Application Insights instance or null if disabled
   */
  getAppInsights(): ApplicationInsights | null {
    return this.appInsights;
  }
}
export { SeverityLevel };

